import { createFileRoute } from "@tanstack/react-router";
import { HardHat, Layers, BarChart3, Users, FileText, ShieldCheck } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/construction-management-software";
const TITLE = "Construction Management Software for Contractors — Business Dite Ads";
const DESC =
  "All-in-one construction management software: projects, crews, time tracking, estimates, invoicing and payroll. Built for contractors. Start free.";

const faqs = [
  {
    q: "What does construction management software do?",
    a: "It centralizes projects, crews, schedules, costs and documents so contractors run jobs profitably from one place — instead of juggling spreadsheets, WhatsApp and paper.",
  },
  {
    q: "Is it built for small and mid-size contractors?",
    a: "Yes. Business Dite Ads is designed for residential and commercial contractors from 3 to 200 employees — framing, concrete, roofing, MEP and general contractors.",
  },
  {
    q: "Can my crew use it on a phone?",
    a: "Yes. Field crews clock in with GPS from any smartphone, no app install required. Office users get a full web dashboard.",
  },
  {
    q: "Do I need a long contract?",
    a: "No. Monthly plans, 14-day free trial and you can cancel anytime.",
  },
];

export const Route = createFileRoute("/construction-management-software")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      {
        rel: "alternate",
        hrefLang: "pt",
        href: "https://busditeads.com/software-de-gestao-para-construcao",
      },
      {
        rel: "alternate",
        hrefLang: "es",
        href: "https://busditeads.com/software-de-gestion-de-construccion",
      },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Construction Management Software"
      h1="Run every job site, crew and dollar from one platform"
      subhead="Business Dite Ads brings projects, time tracking, estimates, invoicing and payroll together — built specifically for construction companies that want profit per project, not just paperwork."
      primaryCtaLabel="Start 14-day free trial"
      secondaryCtaLabel="See pricing"
      benefits={[
        "No credit card required",
        "Setup in under 1 hour",
        "Free onboarding call",
        "Cancel anytime",
      ]}
      featuresHeading="Everything a contractor needs in one dashboard"
      features={[
        {
          icon: HardHat,
          title: "Job sites & projects",
          description:
            "Plan job sites, assign crews, track progress and keep every document attached to the right project.",
        },
        {
          icon: Layers,
          title: "Estimates to invoices",
          description:
            "Turn AI-assisted estimates into signed contracts and immutable invoices — without re-typing a line.",
        },
        {
          icon: BarChart3,
          title: "Profit per project",
          description:
            "See real cost vs. revenue per job: labor, materials, expenses and margin updated live.",
        },
        {
          icon: Users,
          title: "Crew & subcontractors",
          description:
            "Manage employees and subs, hourly or daily pay rates, and roles with granular permissions.",
        },
        {
          icon: FileText,
          title: "Documents & approvals",
          description:
            "Client-side invoice approval, signed contracts and receipts attached to each expense.",
        },
        {
          icon: ShieldCheck,
          title: "Built for compliance",
          description:
            "Audit trail on every change, role-based access and secure cloud storage for tax season.",
        },
      ]}
      howItWorksHeading="From signup to first profitable job in days"
      steps={[
        {
          title: "Import your jobs & crew",
          description:
            "Add active projects, employees and pay rates. We help you migrate from spreadsheets.",
        },
        {
          title: "Crews clock in with GPS",
          description:
            "Field workers clock in from their phone. Office sees hours, locations and costs in real time.",
        },
        {
          title: "Bill, pay and see profit",
          description:
            "Send invoices, run payroll and watch margin per project update automatically.",
        },
      ]}
      socialProofHeading="Why contractors switch to Business Dite Ads"
      socialProof={[
        "Replace 4–5 disconnected tools with one platform",
        "Stop losing hours to manual timesheets and spreadsheets",
        "Catch unprofitable jobs before they cost you money",
        "Pay crews accurately — by the hour or by the day",
        "Get paid faster with client-approved digital invoices",
        "Available in English, Spanish and Portuguese",
      ]}
      faqHeading="Frequently asked questions"
      faqs={faqs}
      finalCtaHeading="Ready to run your construction business like a pro?"
      finalCtaSubhead="Join contractors who replaced paper, spreadsheets and 5 different apps with Business Dite Ads."
    />
  );
}
